import { useState, useRef, useEffect } from "react";
import { Flex, Text, Tabs, TabPanels, TabPanel, TabList, Tab, Button } from "@chakra-ui/react";
import { RModal } from "components";
import { useFunds } from "context/fundsContext";
import { useGetTicketsAvailable, useUserSettings } from "hooks";
import { IconActivity, IconArrow, IconCheckGreen, IconCopyPaste, IconRetryWallet, IconSend, IconWalletBg } from "icons";
import WalletBalance from "../subcomponents/WalletBallance";
import WalletButtons from "../subcomponents/WalletButtons";
import WalletSummary from "../subcomponents/WalletSummary";
import { ActivityTab } from "../ActivityTab";
import Receive from "../subcomponents/Receive";
import Send from "../subcomponents/Send";

interface WalletProps {
  isOpen: boolean;
  onClose: () => void;
}

const Wallet = ({ isOpen, onClose }: WalletProps) => {
  const [activeSection, setActiveSection] = useState<'send' | 'receive' | 'wallet'>('wallet');
  const [activeSendSection, setActiveSendSection] = useState<"send" | "to" | "summary" | "success" | "error" | "transaction">("send");
  const [activeReceiveSection, setActiveReceiveSection] = useState<"receive" | "address">("receive");

  const { balanceData, isLoading: isFundsLoading } = useFunds();
  const { getUserSettings } = useUserSettings();
  const { data: ticketsAvailable, isLoading: isTicketsLoading } = useGetTicketsAvailable();

  const [copied, setCopied] = useState<boolean>(false);
  const [isTransfering, setIsTransfering] = useState<boolean>(false);
  const [isValueValid, setIsValueValid] = useState<boolean>(false);
  const [isAddressValid, setIsAddressValid] = useState<boolean>(false);
  
  const sendComponentRef = useRef<{
    handleSend: () => Promise<void>;
    isValueValid: boolean;
    isAddressValid: boolean;
  } | null>(null);

  // Update validation states from ref when available
  useEffect(() => {
    if (sendComponentRef.current) {
      setIsValueValid(sendComponentRef.current.isValueValid);
      setIsAddressValid(sendComponentRef.current.isAddressValid);
    }
  }, [activeSendSection, sendComponentRef.current?.isValueValid, sendComponentRef.current?.isAddressValid]);

  const handleCopyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Falha ao copiar:', err);
      setCopied(false);
    }
  };

  const handleCloseModal = () => {
    setActiveSection('wallet');
    setActiveSendSection('send');
    onClose();
  }

  const renderModalFooter = () => {
    // Footer for wallet section (no footer needed)
    if (activeSection === 'wallet') return null;

    // Footer for receive section
    if (activeSection === 'receive') {
      return (
        <Button
          variant="blueButton"
          w="full"
          onClick={() => {
            if (activeReceiveSection === "address") {
              setActiveReceiveSection("receive");
            } else {
              setActiveSection("wallet");
            }
          }}
        >
          <IconArrow 
            __css={{
              path: {
                fill: 'rgba(109, 177, 241, 0.8)'
              }
            }}
            w="16px"
            h="16px"
          />
        </Button>
      );
    }

    // Footer for send section
    if (activeSection === 'send') {
      // Different footers based on the send section stage
      switch (activeSendSection) {
        case "send":
          return (
            <Button
              variant="blueButton"
              w="full"
              onClick={() => setActiveSection("wallet")}
            >
              <IconArrow 
                __css={{
                  path: {
                    fill: 'rgba(109, 177, 241, 0.8)'
                  }
                }}
                w="16px"
                h="16px"
              />
            </Button>
          );

        case "to":
          return (
            <Flex gap="2" w="full">
              <Button
                variant="secondary-gradient"
                onClick={() => setActiveSendSection("send")}
              >
                <IconArrow 
                  __css={{
                    path: {
                      fill: 'white.80'
                    }
                  }}
                  w="16px"
                  h="16px"
                />
              </Button>
              
              <Button
                variant="blueButton"
                w="full"
                isDisabled={!isValueValid || !isAddressValid}
                opacity={isValueValid && isAddressValid ? 1 : 0.5}
                onClick={() => setActiveSendSection("summary")}
              >
                NEXT
                <IconArrow 
                  transform="rotate(180deg)"
                  __css={{
                    path: {
                      fill: 'rgba(109, 177, 241, 0.8)'
                    }
                  }}
                  w="16px"
                  h="16px"
                  ml="0.25rem"
                />
              </Button>
            </Flex>
          );

        case "summary":
          return (
            <Flex gap="2" w="full">
              <Button
                variant="secondary-gradient"
                onClick={() => setActiveSendSection("to")}
              >
                <IconArrow 
                  __css={{
                    path: {
                      fill: 'white.80'
                    }
                  }}
                  w="16px"
                  h="16px"
                />
              </Button>
              
              <Button
                variant="blueButton"
                w="full"
                isDisabled={isTransfering}
                onClick={() => sendComponentRef.current?.handleSend()}
              >
                {isTransfering ? "SENDING..." : (
                  <>
                    SEND
                    <IconSend 
                      __css={{
                        path: {
                          fill: 'rgba(109, 177, 241, 0.8)'
                        }
                      }}
                      w="16px"
                      h="16px"
                      ml="0.25rem"
                    />
                  </>
                )}
              </Button>
            </Flex>
          );

        case "success":
        case "transaction":
          return (
            <Button
              variant="blueButton"
              w="full"
              onClick={() => {
                setActiveSection("wallet");
                setActiveSendSection("send");
              }}
            >
              CLOSE
            </Button>
          );

        case "error":
          return (
            <Flex gap="2" w="full">
              <Button
                variant="secondary-gradient"
                onClick={() => setActiveSection("wallet")}
              >
                CLOSE
              </Button>

              <Button
                variant="blueButton"
                w="full"
                onClick={() => setActiveSendSection("to")}
              >
                RETRY
                <IconRetryWallet 
                  __css={{
                    path: {
                      fill: 'white.80'
                    }
                  }}
                  w="16px"
                  h="16px"
                />
              </Button>
            </Flex>
          );

        default:
          return null;
      }
    }

    return null;
  };

  return (
    <RModal
      isOpen={isOpen}
      onClose={handleCloseModal}
      title="Wallet"
      size="2xl"
      modalVariant="blue"
      icon={<IconWalletBg />}
      footer={renderModalFooter()}
    >
        {activeSection === 'wallet' && (
          <Tabs
            w="full"
            variant="tabRaces"
          >
            <TabList>
              <Tab
                gap={2}
                fontSize={{ base: '0.75rem', md: '1rem' }}
                lineHeight={{ base: '1rem', md: '120%' }}
                borderTopRadius="4px"
                fontFamily="heading"
                fontWeight="400"
                w="full"
                px={{ base: 2, md: 4 }}
              >
                Home
              </Tab>
              <Tab
                gap={2}
                fontSize={{ base: '0.80rem', md: '1rem' }}
                lineHeight={{ base: '1rem', md: '120%' }}
                borderTopRadius="4px"
                fontFamily="heading"
                fontWeight="400"
                w="full"
                px={{ base: 2, md: 4 }}
              >
                Activity
              </Tab>
            </TabList>
            <TabPanels>
              <TabPanel p="0" mt="4">
                <Flex
                  flexDir="column"
                  background="linear-gradient(90deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0) 100%)"
                  position="relative"
                  borderRadius="8px"
                  _before={{
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '8px',
                    padding: '2px',
                    background: 'linear-gradient(90deg, rgba(79, 197, 186, 0.6) 0%, rgba(79, 197, 186, 0.12) 100%)',
                    mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
                    maskComposite: 'exclude',
                    zIndex: 0,
                  }}
                  gap="1rem"
                  px="4"
                  py="6"
                >
                  <WalletBalance balanceData={balanceData} isLoading={isFundsLoading} />

                  <WalletButtons setActiveSection={setActiveSection} />

                  <WalletSummary balanceData={balanceData} ticketsAvailable={ticketsAvailable} />
                </Flex>
              </TabPanel>

              <TabPanel p="0" mt="4">
                <Flex
                  flexDir="column"
                  background="linear-gradient(90deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0) 100%)"
                  position="relative"
                  borderRadius="8px"
                  height="480px"
                  overflowY="auto"
                  className="red-scrollbar"
                  _before={{
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '8px',
                    padding: '2px',
                    background: 'linear-gradient(90deg, rgba(79, 197, 186, 0.6) 0%, rgba(79, 197, 186, 0.12) 100%)',
                    mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
                    maskComposite: 'exclude',
                    pointerEvents: 'none',
                  }}
                  gap="0.25rem"
                  px="4"
                  py="6"
                  zIndex={10}
                >
                  <Flex alignItems="center" gap="2">
                    <IconActivity />
                    <Text
                      fontSize={{ base: '14px', md: '18.5px' }}
                      lineHeight={{ base: '18px', md: '24px' }}
                      fontFamily="heading"
                      fontWeight="400"
                      color="white.80"
                      textTransform="uppercase"
                    >
                      Activity
                    </Text>
                  </Flex>
                  <ActivityTab />
                </Flex>
              </TabPanel>
            </TabPanels>
          </Tabs>
        )}

        {activeSection === 'receive' && (
          <Receive 
            setActiveSection={setActiveSection} 
            activeReceiveSection={activeReceiveSection}
            setActiveReceiveSection={setActiveReceiveSection}
          />
        )}

        {activeSection === 'send' && (
          <Send 
            setActiveSection={setActiveSection} 
            balanceData={balanceData}
            activeSendSection={activeSendSection}
            setActiveSendSection={setActiveSendSection}
            setIsTransfering={setIsTransfering}
            isTransfering={isTransfering}
            ref={sendComponentRef}
            setIsValueValid={setIsValueValid}
            setIsAddressValid={setIsAddressValid}
          />
        )}
    </RModal>
  );
};

export default Wallet;